import _ from 'lodash'
import * as THREE from 'three'
import BaseTool from '@glass-designer/tools/baseTool'
import EditingLine from '@glass-designer/objects/editingLine'

import Config from '@glass-designer/config'
import MathsUtils from '@/utils/mathsUtils'

import EditorTypes from '@glass-designer/types'
import AngularDimension from '@glass-designer/objects/angularDimension'
import LinearDimension from '@glass-designer/objects/linearDimension'

/**
  * Base Line tool
  * Base class for all line based tools to inherit from
  */
export default class BaseLineTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.lineDetails = {
      startPointSelected: false,
      endPointSelected: false,
      settingEndPoint: false,
      storedStart: null,
      storedEnd: null,
      storedStartAngleFixed: null,
      storedEndAngleFixed: null,
      startAngleFixed: false,
      endAngleFixed: false,
      start: new THREE.Vector2(0, 0),
      end: new THREE.Vector2(10, 10),
      maxExtentStart: new THREE.Vector2(0, 0),
      maxExtentEnd: new THREE.Vector2(0, 0),
      isHorizontal: true,
      optionIsAxisAligned: false
    }

    this.traceOptions = {
      ignoreDatumLines: false,
      ignoreSplitLines: false,
    }
    this.snapOptions = {
      ignoreDatumLines: false,
      ignoreSegmentEdges: false,
      vertexOnly: false
    }

    this.selectedShape = null
    this.selectedShapeBounds = null
    this.selectedShapeCenter = new THREE.Vector2()

    this.editingDimension = null
    this.editingDimShape = null
    this.editingDimLinkData = null

    this.snapMaxScreenDist = 25
    this.snapAngle = 45
    this.snapMaxAngleDistance = 2.5

    // Setup the cutting line visuals
    this.editingLine = new EditingLine(this.editor, {
      showOutsideTextLabel: true,
      outsideTextLabel: 'Outside',
      showInsideTextLabel: true,
      insideTextLabel: 'Inside',
    })
    this.editingLine.setVisible(false)
    this.editingLine.setEnabled(false)
    this.editingLine.setObjectEventListener(this)
    this.editingLine.startHandle.setObjectEventListener(this)
    this.editingLine.endHandle.setObjectEventListener(this)
    this.addChildObject(this.editingLine)
  }

  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive(options)

    this.editor.setDimensionsEnabled([EditorTypes.DimensionTypes.editingLineDimension])
    this.editor.setShapeSelectionEnabled(false)
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()

    this.selectedShape = null
    this.selectedShapeBounds = null

    this.editingLine.setVisible(false)
    this.editingLine.setEnabled(false)

    this.editor.resetDimensionsEnabled()
    this.editor.resetSelectionEnabled()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()
  }


  // Reset the line details
  resetLineDetails() {
    this.lineDetails.startPointSelected = false
    this.lineDetails.endPointSelected = false
    this.lineDetails.startAngleFixed = false
    this.lineDetails.endAngleFixed = false
    
    this.lineDetails.flipStartAngle = false
    this.lineDetails.flipEndAngle = false

    this.lineDetails.startAngleFixed = false
    this.lineDetails.endAngleFixed = false

    this.editingLine.setVisible(false)
    this.editingLine.setEnabled(false)
    this.editingLine.resetLinkedObjects()

    // Reset dimensions
    if(this.selectedShape)
      this.editor.onToolEditingUpdated(this.selectedShape, [])

    this.selectedShape = null
    this.selectedShapeBounds = null

    this.editor.flagRenderRequired()
  }

  // A dimension used while editing in this tool was clicked
  editingDimensionClicked(dimension) {
    if(dimension.dimType != EditorTypes.DimensionTypes.editingLineDimension)
      return

    this.editingDimension = dimension
    this.editingDimShape = dimension.glassShape
    this.editingDimLinkData = _.cloneDeep(dimension.linkData)
    
    // Store cutting values
    this.lineDetails.storedStart = this.lineDetails.start.clone()
    this.lineDetails.storedEnd = this.lineDetails.end.clone()
    this.lineDetails.storedStartAngleFixed = this.lineDetails.startAngleFixed
    this.lineDetails.storedEndAngleFixed = this.lineDetails.endAngleFixed

    // Angle dimension clicked
    if(dimension instanceof AngularDimension) {

      let isUserFixed = dimension.linkData.isStartLine ? this.lineDetails.startAngleFixed : this.lineDetails.endAngleFixed

      // Fill in options
      let popoverOptions = {
        value: dimension.getAngle(),
        displayUserFixedField: true,
        allowPlusMinus: false,
        isUserFixed: isUserFixed,
        title: 'Angle',
        addon: '°',
        context: this,
        updateCallback: this.updateDimensionEdit,
        cancelCallback: this.cancelDimensionEdit,
        applyCallback: this.applyDimensionEdit
      }

      this.editor.component.displayNumberEntryPopover(popoverOptions)
    }
    
    // Linear dimension
    if(dimension instanceof LinearDimension) {

      // Fill in options
      let popoverOptions = {
        value: dimension.getTotalLength(),
        displayUserFixedField: false,
        allowPlusMinus: false,
        title: 'Length',
        addon: 'mm',
        context: this,
        updateCallback: this.updateDimensionEdit,
        cancelCallback: this.cancelDimensionEdit,
        applyCallback: this.applyDimensionEdit
      }

      this.editor.component.displayNumberEntryPopover(popoverOptions)
    }
  }

  updateDimensionEdit(context, options) {
    if(context.editingDimension) {

      if(context.editingDimension instanceof LinearDimension) {
        context._updateEditingLineFromLinearDimensionEdit(context.editingDimShape, context.editingDimLinkData, options.value)
      }
      if(context.editingDimension instanceof AngularDimension) {
        context._updateEditingLineFromAngularDimensionEdit(context.editingDimShape, context.editingDimLinkData, options.value, options.isUserFixed)
      }
    }
  }
  cancelDimensionEdit(context) {
    if(context.editingDimension) {

      context.lineDetails.start = context.lineDetails.storedStart
      context.lineDetails.end = context.lineDetails.storedEnd
      context.lineDetails.startAngleFixed = context.lineDetails.storedStartAngleFixed
      context.lineDetails.endAngleFixed = context.lineDetails.storedEndAngleFixed
      context.lineDetails.storedStart = null
      context.lineDetails.storedEnd = null

      // Revert back to saved editing line info & rebuild dimensions
      context.traceEditingLine({ updateStartPoint: true, updateEndPoint: true })
      context.updateEditingLineLinkedObjects(true, true)
      context.onEditingLineUpdated()
      context.editor.render()

      // Recalc dimensions
      context.editor.onToolEditingUpdated(context.editingDimShape, [context.editingLine])
    }

    context.editingDimension = null

    context.displayToolSettingsPopover()
  }
  applyDimensionEdit(context, options) {
    if(context.editingDimension) {

      if(context.editingDimension instanceof LinearDimension) {
        context._updateEditingLineFromLinearDimensionEdit(context.editingDimShape, context.editingDimLinkData, options.value)
      }
      if(context.editingDimension instanceof AngularDimension) {
        context._updateEditingLineFromAngularDimensionEdit(context.editingDimShape, context.editingDimLinkData, options.value, options.isUserFixed)
      }
      context.editingDimension = null
    }
    context.displayToolSettingsPopover()
  }

  // Update the editing line from a linear dimension editing
  _updateEditingLineFromLinearDimensionEdit(shape, dimensionLinkData, newLength) {
    let refPoint = dimensionLinkData.refPoint
    let refDirection = dimensionLinkData.refDir

    var lineMid = new THREE.Vector2((this.lineDetails.start.x + this.lineDetails.end.x) / 2.0, (this.lineDetails.start.y + this.lineDetails.end.y) / 2.0)

    let newPosition = new THREE.Vector2(refPoint.x, refPoint.y)
    newPosition.addScaledVector(new THREE.Vector2(refDirection.x, refDirection.y), newLength)

    // If perpendicular to an arc, move the position to that
    if(dimensionLinkData.isPerpendicularToArc && this.editingLine.linkedStartObj) {
      let traceDir = new THREE.Vector2(-refDirection.y, refDirection.x)
      let intList = this.editingLine.linkedStartObj.calculateLineIntersection(newPosition, newPosition.clone().addScaledVector(traceDir, this.editingLine.linkedStartObj.getLength() * 2))

      if(intList && intList.length > 0) {
        intList = MathsUtils.sort2DPointListByDirection(intList, traceDir)
        newPosition.set(intList[0].x, intList[0].y)
      }
    }
    // If perpendicular to an arc, move the position to that
    if(dimensionLinkData.isPerpendicularToArc && this.editingLine.linkedEndObj) {
      let traceDir = new THREE.Vector2(-refDirection.y, refDirection.x)
      traceDir.negate()
      let intList = this.editingLine.linkedEndObj.calculateLineIntersection(newPosition, newPosition.clone().addScaledVector(traceDir, this.editingLine.linkedEndObj.getLength() * 2))

      if(intList && intList.length > 0) {
        intList = MathsUtils.sort2DPointListByDirection(intList, traceDir)
        newPosition.set(intList[0].x, intList[0].y)
      }
    }

    let traceOptions = {
      updateStartPoint: dimensionLinkData.isStartLine,
      updateEndPoint: !dimensionLinkData.isStartLine,
      referencePoint: lineMid.clone()
    }

    // If either end of the cutting line is fixed angle, move both points when editing linear dimensions
    let lineFixed = this.lineDetails.startAngleFixed || this.lineDetails.endAngleFixed

    if(dimensionLinkData.isStartLine) {
      if(lineFixed) {
        let moveVec = newPosition.clone().sub(this.lineDetails.start)
        this.lineDetails.end.add(moveVec)
        traceOptions.updateEndPoint = true
      }
      this.lineDetails.start.set(newPosition.x, newPosition.y)

      let newDir = this.lineDetails.end.clone().sub(this.lineDetails.start)
      newDir.normalize()
      traceOptions.referencePoint = this.lineDetails.start.clone().addScaledVector(newDir, 5)

    } else {
      if(lineFixed) {
        let moveVec = newPosition.clone().sub(this.lineDetails.end)
        this.lineDetails.start.add(moveVec)
        traceOptions.updateStartPoint = true
      }

      this.lineDetails.end.set(newPosition.x, newPosition.y)

      let newDir = this.lineDetails.end.clone().sub(this.lineDetails.start)
      newDir.normalize()
      traceOptions.referencePoint = this.lineDetails.end.clone().addScaledVector(newDir, -5)
    }

    this.traceEditingLine(traceOptions)
    this.updateEditingLineLinkedObjects(!dimensionLinkData.isStartLine, dimensionLinkData.isStartLine)
    this.onEditingLineUpdated()
    this.editor.render()

    // Recalc dimensions
    this.editor.onToolEditingUpdated(shape, [this.editingLine])
  }

  // Update the editing line from an angular dimension editing
  _updateEditingLineFromAngularDimensionEdit(shape, dimensionLinkData, newAngle, userFixed) {

    let lineDir = this.lineDetails.end.clone().sub(this.lineDetails.start)
    var lineMid = new THREE.Vector2((this.lineDetails.start.x + this.lineDetails.end.x) / 2.0, (this.lineDetails.start.y + this.lineDetails.end.y) / 2.0)
    let editingLineLength = lineDir.length()
    lineDir.normalize()
    let traceRefPoint = lineMid.clone()

    let newAngleRads = MathsUtils.degreesToRadians(newAngle)
    let refAngle = dimensionLinkData.refAngle
    let refPoint = dimensionLinkData.isStartLine ? this.lineDetails.start : this.lineDetails.end
  
    if(dimensionLinkData.isStartLine) {
      let newPosition = refPoint.clone()

      let newDir = MathsUtils.generate2DDirectionVector(newAngleRads + refAngle)
      traceRefPoint = this.lineDetails.start.clone().addScaledVector(newDir, 5)

      newPosition.addScaledVector(newDir, editingLineLength)
      this.lineDetails.end.set(newPosition.x, newPosition.y)
      this.lineDetails.startAngleFixed = userFixed

    } else {
      let newPosition = refPoint.clone()

      let newDir = MathsUtils.generate2DDirectionVector(newAngleRads + refAngle)
      traceRefPoint = this.lineDetails.end.clone().addScaledVector(newDir, 5)

      newPosition.addScaledVector(newDir, editingLineLength)
      this.lineDetails.start.set(newPosition.x, newPosition.y)
      this.lineDetails.endAngleFixed = userFixed
    }

    this.traceEditingLine({ updateStartPoint: !dimensionLinkData.isStartLine, updateEndPoint: dimensionLinkData.isStartLine, referencePoint: traceRefPoint })
    this.updateEditingLineLinkedObjects(!dimensionLinkData.isStartLine, dimensionLinkData.isStartLine)
    this.onEditingLineUpdated()
    this.editor.render()

    // Recalc dimensions
    this.editor.onToolEditingUpdated(shape, [this.editingLine])
  }


  /**
   * Update the start/end objects the editing line is linked to on the selected shape
   * Sets to null if the points are not on any segment/datum object
   * Used for dimension calculations
   */
  updateEditingLineLinkedObjects(updateStart = true, updateEnd = true) {
    let nearPointThreshold = 1.0

    if(updateStart) {
      this.editingLine.setStartLinkedObject(null)

      let startEdgeInfo = this.selectedShape.calculateClosestEdgePoint(this.editingLine.startPoint, { ignoreSplitLines: true, ignoreHoles: true })
      if(startEdgeInfo) {
        if(startEdgeInfo.point.distanceTo(this.editingLine.startPoint) <= nearPointThreshold) {
          if(startEdgeInfo.segmentId) {
            let segment = this.selectedShape.getSegmentById(startEdgeInfo.segmentId)
            this.editingLine.setStartLinkedObject(segment)
          }
          if(startEdgeInfo.datumLineId) {
            let datumLine = this.selectedShape.getDatumLineById(startEdgeInfo.datumLineId)
            this.editingLine.setStartLinkedObject(datumLine)
          }
          if(startEdgeInfo.splitLineId) {
            let splitLine = this.selectedShape.getSplitLineById(startEdgeInfo.splitLineId)
            this.editingLine.setStartLinkedObject(splitLine)
          }
        }
      }
    }

    if(updateEnd) {
      this.editingLine.setEndLinkedObject(null)

      let endEdgeInfo = this.selectedShape.calculateClosestEdgePoint(this.editingLine.endPoint, { ignoreSplitLines: true, ignoreHoles: true })
      if(endEdgeInfo) {
        if(endEdgeInfo.point.distanceTo(this.editingLine.endPoint) <= nearPointThreshold) {
          if(endEdgeInfo.segmentId) {
            let segment = this.selectedShape.getSegmentById(endEdgeInfo.segmentId)
            this.editingLine.setEndLinkedObject(segment)
          }
          if(endEdgeInfo.datumLineId) {
            let datumLine = this.selectedShape.getDatumLineById(endEdgeInfo.datumLineId)
            this.editingLine.setEndLinkedObject(datumLine)
          }
          if(endEdgeInfo.splitLineId) {
            let splitLine = this.selectedShape.getSplitLineById(endEdgeInfo.splitLineId)
            this.editingLine.setEndLinkedObject(splitLine)
          }
        }
      }
    }
  }


  /**
   * Snap a point to the nearest point on a shape
   * Returns a Vector2 object for the nearest point
   */
  snapToNearestShapePoint(shape, position) {
    var renderScale = 1.0 / this.editor.camera.zoom
    var maxSnapDist = this.snapMaxScreenDist * renderScale

    if(!this.editor.getOptionSnapEnabled())
      return position

    var nearestVertexDist = -1
    var nearestVertex = new THREE.Vector2(0, 0)

    // Snap to nearest vertex
    shape.segmentList.forEach(segment => {
      var startDist = segment.startPosition.distanceTo(position)
      var endDist = segment.endPosition.distanceTo(position)

      if((startDist < maxSnapDist || this.snapOptions.vertexOnly) && (startDist < nearestVertexDist || nearestVertexDist < 0)) {
        nearestVertex.copy(segment.startPosition)
        nearestVertexDist = startDist
      }
      if((endDist < maxSnapDist || this.snapOptions.vertexOnly) && (endDist < nearestVertexDist || nearestVertexDist < 0)) {
        nearestVertex.copy(segment.endPosition)
        nearestVertexDist = endDist
      }
    })

    // Snap to nearest datum line points
    if(!this.snapOptions.ignoreDatumLines) {
      shape.getDatumLineList().forEach(datumLine => {
        var startDist = datumLine.startPosition.distanceTo(position)
        var endDist = datumLine.endPosition.distanceTo(position)

        if(startDist < maxSnapDist && (startDist < nearestVertexDist || nearestVertexDist < 0)) {
          nearestVertex.copy(datumLine.startPosition)
          nearestVertexDist = startDist
        }
        if(endDist < maxSnapDist && (endDist < nearestVertexDist || nearestVertexDist < 0)) {
          nearestVertex.copy(datumLine.endPosition)
          nearestVertexDist = endDist
        }
      })
    }


    if(nearestVertexDist >= 0)
      return nearestVertex

    if(!this.snapOptions.ignoreSegmentEdges) {
      let closestInfo = shape.calculateClosestEdgePoint(position, { holeRefPointOnly: true, ignoreSplitLines: true })
      if(closestInfo) {
        let edgeDist = closestInfo.point.distanceTo(position)
        if(edgeDist < maxSnapDist)
          return closestInfo.point
      }
    }

    return position
  }


  /**
   * Snap the line start/end point so the line is to the nearest 45 degrees if available
   */
  snapLineToAngle(currentSnapPoint, isEnd, position) {
    var renderScale = 1.0 / this.editor.camera.zoom
    var maxSnapDist = this.snapMaxScreenDist * renderScale

    if(!this.editor.getOptionSnapEnabled())
      return currentSnapPoint

    var startPoint = new THREE.Vector2(this.lineDetails.start.x, this.lineDetails.start.y)
    var endPoint = new THREE.Vector2(this.lineDetails.end.x, this.lineDetails.end.y)

    var dir = new THREE.Vector2(position.x - this.lineDetails.start.x, position.y - this.lineDetails.start.y)
    if(!isEnd)
      dir = new THREE.Vector2(endPoint.x - position.x, endPoint.y - position.y)
    let lineDistance = dir.length()
    dir.normalize()

    var lineAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(dir)))
    let snapAngle = Math.round(lineAngle / this.snapAngle) * this.snapAngle

    if(MathsUtils.calculateShortestAngleDistanceDegrees(lineAngle, snapAngle) < this.snapMaxAngleDistance) {
      var closestPoint = null

      if(isEnd)
        closestPoint = startPoint.addScaledVector(MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(snapAngle)), lineDistance)
      else
        closestPoint = endPoint.addScaledVector(MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(snapAngle)), -lineDistance)

      // Check if new position is near edge of shape
      let closestInfo = this.selectedShape.calculateClosestEdgePoint(closestPoint, { holeRefPointOnly: true, ignoreSplitLines: true })
      if(closestInfo) {
        let edgeDist = closestInfo.point.distanceTo(closestPoint)

        if(edgeDist < maxSnapDist) {
          currentSnapPoint.set(closestInfo.point.x, closestInfo.point.y)
          return currentSnapPoint
        }
      }

      currentSnapPoint.set(closestPoint.x, closestPoint.y)
    }

    return currentSnapPoint
  }


  /** 
   * Run a raytrace on the start/end points of the measure line to find the intersections
   * Sets the visual editing line based on the first & last intersections found
   */
  traceEditingLine(options = {}) {

    let shapeSize = new THREE.Vector2()
    this.selectedShapeBounds.getSize(shapeSize)
    let rayTraceMaxLength = Math.max(shapeSize.x, shapeSize.y) * 2

    var lineStart = this.lineDetails.start.clone()
    var lineEnd = this.lineDetails.end.clone()
    var lineDir = lineEnd.clone().sub(lineStart)
    var lineMid = new THREE.Vector2((lineStart.x + lineEnd.x) / 2.0, (lineStart.y + lineEnd.y) / 2.0)
    lineDir.normalize()

    let lineDir2D = new THREE.Vector2(lineDir.x, lineDir.y)

    // If grabbed, use that as the inner point to calculate intersections
    if(options.referencePoint)
      lineMid.set(options.referencePoint.x, options.referencePoint.y)
    
    // Cast ray way out of shape bounds
    lineStart.addScaledVector(lineDir, -rayTraceMaxLength)
    lineEnd.addScaledVector(lineDir, rayTraceMaxLength)

    var intersectionOptions = {
      ignoreDatumLines: this.traceOptions.ignoreDatumLines,
      ignoreSplitLines: this.traceOptions.ignoreSplitLines,
    }

    let intersections = this.selectedShape.calculateIntersectionWithLine(lineStart, lineEnd, intersectionOptions)
    if(intersections.length >= 2) {
      var i = 0

      // Find the inner intersections
      var startIndex = 0
      var endIndex = intersections.length-1
      for(i=0; i<intersections.length; i++) {
        if(MathsUtils.calculateDistanceToPlane2D(lineDir2D, lineMid, intersections[i]) < 0)
          startIndex = i
      }
      for(i=intersections.length-1; i>=0; i--) {
        if(MathsUtils.calculateDistanceToPlane2D(lineDir2D, lineMid, intersections[i]) > 0)
          endIndex = i
      }

      this.lineDetails.maxExtentStart.copy(intersections[0])
      this.lineDetails.maxExtentEnd.copy(intersections[intersections.length - 1])

      if(startIndex != endIndex) {
        if(options.updateStartPoint) {
          if(options.roundPoint)
            intersections[startIndex].round()

          this.editingLine.setStartPoint(intersections[startIndex])
          this.lineDetails.start.copy(intersections[startIndex])
        }

        if(options.updateEndPoint) {
          if(options.roundPoint)
            intersections[endIndex].round()

          this.editingLine.setEndPoint(intersections[endIndex])
          this.lineDetails.end.copy(intersections[endIndex])
        }
        
        this.editingLine.startAngleFixed = this.lineDetails.startAngleFixed
        this.editingLine.endAngleFixed = this.lineDetails.endAngleFixed
      }
    }
  }


  // Editing line updated event
  onEditingLineUpdated() {
  }

}
